import type {
  InvoicePromotionPackagesResponse,
  Package,
  PackagesListResponse,
} from './api';

export type PromotionPackagePeriod = {
  days: number;
  discountPercent: number | null;
  originalPrice: number;
  price: number;
};

export type PromotionPackage = {
  name: string;
  periods: PromotionPackagePeriod[];
};

export type PurchasePackageViewModel = {
  benefitPercent: number | null;
  discount: number | null;
  isHighlighted: boolean;
  name: string;
  originalPrice: number | null;
  paidAdsCount: number | null;
  periodDays: number;
  price: number;
  promoPrice: number | null;
  promotionDiscountAmount: number | null;
  promotionDiscountPercent: number | null;
  showPromotionLabel: boolean;
  showPromoPriceLabel: boolean;
  walletAmount: number | null;
};

export function getPackagePriceDisplayState(item: PurchasePackageViewModel) {
  const hasPromoPriceLabel =
    item.showPromoPriceLabel && item.benefitPercent !== null;

  return {
    hasCombinedLabels: hasPromoPriceLabel && item.showPromotionLabel,
    hasOriginalPrice:
      item.originalPrice !== null &&
      (hasPromoPriceLabel || item.showPromotionLabel),
    hasPromoPriceLabel,
  };
}

export function getPackages(data: PackagesListResponse | undefined): Package[] {
  return data?.packages.flatMap(group => group.packages) ?? [];
}

export function getPackagePeriods(packages: Package[]): number[] {
  const periods = packages.flatMap(item =>
    item.periods.flatMap(period =>
      period.value === null || period.value === undefined ? [] : [period.value]
    )
  );

  return Array.from(new Set(periods)).sort((first, second) => first - second);
}

export function getPromotionPeriods(packages: Package[]): number[] {
  const periods = packages.flatMap(item =>
    item.periods.flatMap(period => {
      if (!period.showPromotionLabel || period.value == null) return [];
      return [period.value];
    })
  );

  return Array.from(new Set(periods)).sort((first, second) => first - second);
}

export function mapPurchasePackage(
  item: Package,
  periodDays: number
): PurchasePackageViewModel {
  const selectedPeriod = item.periods.find(
    period => period.value === periodDays
  );
  const durationMultiplier = periodDays / 30;
  const packageName = item.name.trim().toLowerCase();

  return {
    benefitPercent: selectedPeriod?.promoPriceDiscount ?? null,
    discount: item.discount ?? null,
    isHighlighted: packageName === 'platinum+',
    name: item.name,
    originalPrice: selectedPeriod?.originalPrice ?? null,
    paidAdsCount:
      item.paidAdsCount != null ? item.paidAdsCount * durationMultiplier : null,
    periodDays,
    price: selectedPeriod?.price ?? 0,
    promoPrice: selectedPeriod?.promoPrice ?? null,
    promotionDiscountAmount: selectedPeriod?.promotionDiscountAmount ?? null,
    promotionDiscountPercent: selectedPeriod?.promotionDiscount ?? null,
    showPromotionLabel: Boolean(selectedPeriod?.showPromotionLabel),
    showPromoPriceLabel: Boolean(selectedPeriod?.showPromoPriceLabel),
    walletAmount:
      item.walletAmount != null ? item.walletAmount * durationMultiplier : null,
  };
}

export function getPromotionPackages(
  data: InvoicePromotionPackagesResponse | undefined
): PromotionPackage[] {
  return (data?.packages ?? [])
    .flatMap(group => group.packages)
    .filter(item => item.periods.some(period => period.showPromotionLabel))
    .map(item => ({
      name: item.name,
      periods: item.periods
        .flatMap(period => {
          if (period.value == null) {
            return [];
          }

          const originalPrice = period.originalPrice ?? period.price;
          const hasDiscount = originalPrice > period.price;

          return [
            {
              days: period.value,
              discountPercent: hasDiscount
                ? ((originalPrice - period.price) / originalPrice) * 100
                : null,
              originalPrice,
              price: period.price,
            },
          ];
        })
        .sort((first, second) => first.days - second.days),
    }));
}
